import React from 'react';
import Section from '../layout/Section';
import SectionTitle from '../ui/SectionTitle';
import AnimatedCounter from '../ui/AnimatedCounter';
import StaggerContainer, { staggerItem } from '../animations/StaggerContainer';
import { motion } from 'motion/react';
import ScrollReveal from '../animations/ScrollReveal';

const problems = [
  {
    icon: '💸',
    title: 'Écarts de caisse',
    desc: "Le soir, vous comptez votre caisse et il manque 15 000 FCFA. Erreur de monnaie ? Vente non notée ? Vous ne saurez jamais.",
  },
  {
    icon: '📝',
    title: 'Dettes oubliées',
    desc: "Un client fidèle vous dit : \"Je t'ai déjà payé.\" Pas de preuve. Vous le croyez. Vous perdez 50 000 FCFA.",
  },
  {
    icon: '📦',
    title: 'Ruptures de stock',
    desc: "Vous refusez une vente parce que vous pensiez avoir le produit. Le client va chez le concurrent.",
  },
  {
    icon: '📉',
    title: 'Zéro visibilité',
    desc: "Fin du mois : vous avez travaillé dur, mais vous ne savez pas si vous avez gagné ou perdu de l'argent.",
  },
];

const solutions = [
  { icon: '⏱', value: '30s', label: 'Enregistrez une vente' },
  { icon: '👆', value: '1 clic', label: 'Voyez qui vous doit' },
  { icon: '📡', value: 'Temps réel', label: 'Suivez votre caisse' },
  { icon: '🌙', value: 'Chaque soir', label: 'Savez combien vous gagnez' },
];

const metrics = [
  { value: 30, suffix: 'sec', label: 'par vente' },
  { value: 0, label: 'erreurs de caisse' },
  { prefix: '~', value: 0, suffix: '%', label: 'dettes perdues' },
  { value: 5, suffix: 'min', label: 'de comptabilité/jour' },
  { value: 100, suffix: '%', label: 'visibilité' },
];

export default function ProblemSection() {
  return (
    <Section dark id="problem">
      {/* Problem Cards */}
      <SectionTitle title="Arrêtez de perdre de l'argent chaque jour." dark />

      <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {problems.map((problem) => (
          <motion.div
            key={problem.title}
            variants={staggerItem}
            className="glass rounded-xl p-4"
          >
            <div className="text-2xl mb-1">{problem.icon}</div>
            <h3 className="text-white font-bold text-sm mb-1">{problem.title}</h3>
            <p className="text-gray-400 text-sm">{problem.desc}</p>
          </motion.div>
        ))}
      </StaggerContainer>

      <ScrollReveal>
        <div className="glass rounded-xl p-3 text-center mt-4 border border-primary/20">
          <div className="text-2xl lg:text-3xl font-black text-primary">
            100 000 — 300 000 FCFA
          </div>
          <p className="text-gray-400 mt-1">
            perdus chaque mois — dettes oubliées, écarts de caisse, ruptures de stock
          </p>
          <p className="text-gray-500 text-sm mt-1">
            Soit 1,2 à 3,6 millions FCFA par an. XamXamBoutik coûte une fraction de ce que vous perdez déjà.
          </p>
        </div>
      </ScrollReveal>

      {/* Solution */}
      <div className="mt-4">
        <ScrollReveal>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-center gradient-text-orange mb-3">
            XamXamBoutik change tout.
          </h2>
        </ScrollReveal>

        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {solutions.map((item) => (
            <motion.div
              key={item.label}
              variants={staggerItem}
              className="text-center glass rounded-xl p-4 border border-primary/10"
            >
              <div className="text-2xl mb-1">{item.icon}</div>
              <div className="text-xl font-black text-primary mb-1">{item.value}</div>
              <p className="text-gray-400 text-sm">{item.label}</p>
            </motion.div>
          ))}
        </StaggerContainer>
      </div>

      {/* Metrics Bar */}
      <div className="mt-3 rounded-xl bg-gradient-to-r from-primary to-orange-500 p-3 lg:p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-2">
          {metrics.map((metric, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl lg:text-3xl font-black text-white flex items-baseline justify-center gap-1">
                {metric.prefix && <span>{metric.prefix}</span>}
                <AnimatedCounter end={metric.value} className="inline" />
                {metric.suffix && (
                  <span className="text-lg lg:text-xl font-bold text-white/80">
                    {metric.suffix}
                  </span>
                )}
              </div>
              <p className="text-white/80 text-xs mt-1 font-medium">
                {metric.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
